import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Card, Form, Input, Button, Alert, Typography } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { loginApi } from '../../api/auth.api';
import useAuthStore from '../../store/useAuthStore';

const { Title } = Typography;

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #e0f2fe 0%, #ffffff 100%)', // Light blue/white gradient
    padding: '20px',
  },
  card: {
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    borderRadius: '12px',
  },
  logo: {
    textAlign: 'center',
    marginBottom: '24px',
    margin: 0,
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1f2937'
  }
};

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);

  const { control, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      loginID: '',
      password: ''
    }
  });

  const onSubmit = async (data) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const response = await loginApi(data);
      
      // Axios interceptor returns the unwrapped data (LoginResponseDto)
      const { token, ...user } = response;
      
      // Set auth state. App.jsx will automatically react and apply the themeColor
      setAuth(token, user);
      
      // Navigate to dashboard
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setErrorMsg(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <Card style={styles.card} bordered={false}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <Title level={3} style={styles.logo}>🏭 Manufacturing IMS</Title>
        </div>

        {errorMsg && (
          <Alert 
            title={errorMsg} 
            type="error" 
            showIcon 
            style={{ marginBottom: '20px' }} 
          />
        )}

        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <Form.Item
            validateStatus={errors.loginID ? 'error' : ''}
            help={errors.loginID?.message}
          >
            <Controller
              name="loginID"
              control={control}
              rules={{ required: 'Login ID is required' }}
              render={({ field }) => (
                <Input 
                  {...field} 
                  size="large" 
                  prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} 
                  placeholder="Login ID" 
                  autoFocus
                />
              )}
            />
          </Form.Item>

          <Form.Item
            validateStatus={errors.password ? 'error' : ''}
            help={errors.password?.message}
          >
            <Controller
              name="password"
              control={control}
              rules={{ required: 'Password is required' }}
              render={({ field }) => (
                <Input.Password 
                  {...field} 
                  size="large" 
                  prefix={<LockOutlined style={{ color: '#bfbfbf' }} />} 
                  placeholder="Password" 
                />
              )}
            />
          </Form.Item>

          <Form.Item style={{ marginTop: '32px', marginBottom: 0 }}>
            <Button 
              type="primary" 
              htmlType="submit" 
              size="large" 
              block 
              loading={loading}
            >
              Login
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default LoginPage;